import { useState } from 'react'
import {
  archiveIntegre,
  cloturesActives,
  ecrituresJusquA,
  empreinte,
  exerciceOuvert,
  instantane,
} from '../../api/cloture'
import { cloturerExercice, rouvrirExercice } from '../../api/operations'
import type { Cloture as TCloture } from '../../api/types'
import { useDroits } from '../Contexte'
import { dateCourte, fcfa } from '../Format'
import { Icone } from '../Icone'
import { Modale } from '../Modale'
import { Carte, CodeCompte, Ligne, Table, type PropsCompta } from './briques'

/**
 * Clôture d'exercice — verrouillage et archivage (transposition de
 * `renderCloture` / `closureModal` / `closureConfirm` / `closureReopen` /
 * `closureView`).
 *
 * ⚠️ Différence assumée : l'original écrivait d'abord sur le POSTE
 * (localStorage) puis tentait la base, et en cas d'échec annonçait « clôture
 * conservée sur ce poste ». Une clôture qui n'existe que dans un navigateur
 * ne verrouille rien pour les collègues. Ici, la clôture EST l'insertion en
 * base (tables en ajout seul, horodatage serveur) — ou elle n'a pas lieu.
 */
export function Cloture({ donnees, orgId, ecritures, verrou, surNotifier, surRafraichir }: PropsCompta) {
  const droits = useDroits()
  const plan = donnees.planComptable
  const actives = cloturesActives(donnees.clotures, donnees.evenementsCloture)
  const annee = exerciceOuvert(ecritures, actives)
  const s = instantane(ecritures, annee, plan)
  const equilibre = s.totalDebit === s.totalCredit
  const [confirmer, setConfirmer] = useState(false)
  const [vue, setVue] = useState<TCloture | null>(null)
  const [enCours, setEnCours] = useState(false)

  const idsActifs = new Set(actives.map((c) => c.id))
  const archives = [...donnees.clotures].sort((a, b) => b.annee - a.annee || b.clotureLe.localeCompare(a.clotureLe))
  const piste = [...donnees.evenementsCloture].sort((a, b) => b.le.localeCompare(a.le)).slice(0, 20)

  async function cloturer() {
    if (!equilibre) return surNotifier('Corrigez le déséquilibre avant de clôturer')
    setEnCours(true)
    try {
      await cloturerExercice(orgId, {
        annee,
        fin: s.fin,
        resultat: s.resultat,
        produits: s.produits,
        charges: s.charges,
        balance: s.balance,
        empreinte: empreinte(annee, s.fin, s.resultat, s.balance),
        detail: `Résultat net ${fcfa(s.resultat)} · ${s.balance.length} comptes archivés`,
      })
      surNotifier(`Exercice ${annee} clôturé et archivé`)
      setConfirmer(false)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Clôture non enregistrée : ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setEnCours(false)
    }
  }

  async function rouvrir(a: number) {
    if (
      !window.confirm(
        `Rouvrir l'exercice ${a} ?\n\nLe verrouillage sera levé. L'archive n'est PAS supprimée : elle reste consultable et la réouverture est inscrite dans la piste d'audit.`,
      )
    )
      return
    try {
      await rouvrirExercice(orgId, a, 'Verrouillage levé, écritures de nouveau modifiables')
      surNotifier(`Exercice ${a} rouvert`)
      await surRafraichir()
    } catch (e) {
      surNotifier(`Réouverture non enregistrée : ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  return (
    <>
      {!donnees.synchro.clotures && (
        <div className="bandeau erreur" style={{ marginBottom: 14 }}>
          Les archives de clôture n’ont pas pu être lues (droits ou réseau). Le verrouillage affiché peut être incomplet :
          ne clôturez pas avant d’avoir rechargé la page avec succès.
        </div>
      )}
      <Carte
        titre={`Exercice en cours — ${annee}`}
        icone="clock"
        sous={`Du 01/01/${annee} au 31/12/${annee} · ${ecrituresJusquA(ecritures, s.fin).length} mouvements cumulés`}
        actions={
          droits.peut('cloture') ? (
            <button className="btn btn-gold btn-sm" onClick={() => setConfirmer(true)} disabled={!donnees.synchro.clotures}>
              <Icone nom="check" /> Clôturer {annee}
            </button>
          ) : (
            <span className="t-muted">{droits.refus}</span>
          )
        }
        flush={false}
      >
        <Ligne libelle="Produits cumulés (classe 7)" valeur={<span className="num-c">{fcfa(s.produits)}</span>} />
        <Ligne libelle="Charges cumulées (classe 6)" valeur={<span className="num-d">{fcfa(s.charges)}</span>} />
        <Ligne libelle="Résultat provisoire" valeur={fcfa(s.resultat)} fort />
        <Ligne
          libelle="Total débit / crédit"
          valeur={
            <>
              {fcfa(s.totalDebit)} / {fcfa(s.totalCredit)} {equilibre ? '— équilibré' : <span style={{ color: 'var(--err)' }}>— déséquilibré</span>}
            </>
          }
        />
        <div className="dash-note">
          {verrou ? (
            <>
              Écritures verrouillées jusqu’au <b>{dateCourte(verrou)}</b> inclus : aucune écriture manuelle ni dépense ne peut
              être datée avant.
            </>
          ) : (
            'Aucun verrouillage actif.'
          )}
        </div>
      </Carte>

      <Carte titre="Exercices clôturés et archivés" icone="book" sous={`${archives.length} archive${archives.length > 1 ? 's' : ''} · balance figée et empreinte d’intégrité`}>
        <Table
          entetes={['Exercice', 'Clôturé le', 'État', 'Résultat net', 'Comptes', 'Empreinte', '']}
          droite={[3]}
          vide="Aucun exercice clôturé."
          lignes={archives.map((c) => {
            const active = idsActifs.has(c.id)
            return [
              <span className="t-strong">{c.annee}</span>,
              <span className="t-muted">{dateCourte(c.clotureLe)}</span>,
              active ? <span className="badge b-green plain">Verrouillé</span> : <span className="badge b-warn plain">Rouvert ou remplacé</span>,
              <span className="mono">{fcfa(c.resultat)}</span>,
              String(c.nbComptes),
              <span className="acc-code">{c.empreinte ?? '—'}</span>,
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setVue(c)}>
                  Balance
                </button>
                {active && droits.peut('cloture') && (
                  <button className="btn btn-outline btn-sm" style={{ color: 'var(--err)' }} onClick={() => void rouvrir(c.annee)}>
                    Rouvrir
                  </button>
                )}
              </span>,
            ]
          })}
        />
      </Carte>

      <Carte titre="Piste d’audit" icone="clock" sous="Clôtures et réouvertures, horodatées par le serveur">
        <Table
          entetes={['Horodatage', 'Opération', 'Exercice', 'Détail']}
          vide="Aucune opération enregistrée."
          lignes={piste.map((x) => [
            <span className="t-muted">
              {dateCourte(x.le)} {new Date(x.le).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>,
            x.action === 'cloture' ? <span className="badge b-green plain">Clôture</span> : <span className="badge b-warn plain">Réouverture</span>,
            <span className="t-strong">Exercice {x.annee}</span>,
            <span className="t-muted">{x.detail}</span>,
          ])}
        />
      </Carte>
      <div className="dash-note">
        La clôture fige la balance au 31/12 et verrouille les écritures antérieures. Les archives sont conservées par la base
        en AJOUT SEUL : ni modifiables ni supprimables, même après une réouverture — laquelle est tracée ci-dessus.
      </div>

      {confirmer && (
        <Modale
          titre={`Clôturer l'exercice ${annee}`}
          ouverte
          surFermer={() => setConfirmer(false)}
          surEnregistrer={() => void cloturer()}
          libelleEnregistrer={equilibre ? 'Clôturer définitivement' : 'Clôture impossible'}
          enCours={enCours}
        >
          <div className="dash-note" style={{ margin: '0 0 12px' }}>
            Balance arrêtée au <b>31/12/{annee}</b> · {s.balance.length} comptes mouvementés.
          </div>
          <Ligne libelle="Produits (classe 7)" valeur={<span className="num-c">{fcfa(s.produits)}</span>} />
          <Ligne libelle="Charges (classe 6)" valeur={<span className="num-d">{fcfa(s.charges)}</span>} />
          <Ligne libelle="Résultat net" valeur={fcfa(s.resultat)} fort />
          <Ligne libelle="Total débit / crédit" valeur={`${fcfa(s.totalDebit)} / ${fcfa(s.totalCredit)}`} />
          <div className={`bandeau${equilibre ? '' : ' erreur'}`} style={{ marginTop: 12 }}>
            {equilibre
              ? `Après clôture, aucune écriture manuelle ni dépense datée du 31/12/${annee} ou avant ne pourra être ajoutée depuis la console. L'opération reste réversible, et la réouverture est tracée.`
              : 'Débit ≠ crédit : corrigez le déséquilibre avant de clôturer, sinon la balance archivée serait incohérente.'}
          </div>
        </Modale>
      )}

      {vue && (
        <Modale titre={`Balance archivée — exercice ${vue.annee}`} ouverte surFermer={() => setVue(null)}>
          <div className="dash-note" style={{ margin: '0 0 10px' }}>
            Arrêtée au {dateCourte(vue.dateVerrou)} · empreinte <b>{vue.empreinte ?? '—'}</b> ·{' '}
            {archiveIntegre(vue) ? (
              <b style={{ color: 'var(--green)' }}>intégrité vérifiée</b>
            ) : (
              <b style={{ color: 'var(--err)' }}>empreinte non concordante — archive à examiner</b>
            )}
          </div>
          <Table
            entetes={['Compte', 'Libellé', 'Débit', 'Crédit', 'Solde']}
            droite={[2, 3, 4]}
            vide="Archive vide"
            lignes={vue.balance.map((r) => [
              <CodeCompte code={r.code} />,
              <span className="t-muted">{r.label}</span>,
              <span className="num-d">{r.debit ? fcfa(r.debit) : ''}</span>,
              <span className="num-c">{r.credit ? fcfa(r.credit) : ''}</span>,
              <b>{fcfa(r.solde)}</b>,
            ])}
          />
        </Modale>
      )}
    </>
  )
}
